import React, { useState, useEffect, useMemo } from 'react';
import { Mountain, Download, X } from 'lucide-react';
import { TASPlot } from '../components/Charts/TASPlot';
import { AFMPlot } from '../components/Charts/AFMPlot';
import { RockTypeDistributionChart } from '../components/Charts/RockTypeDistributionChart';
import { HarkerDiagrams } from '../components/Charts/HarkerDiagrams';
import { exportSamplesToCSV } from '../utils/csvExport';
import {
  hasAfmOxides,
  hasTasOxides,
  toHarkerDataPoint,
  transformChemicalAnalysisSamples,
  type ChemicalAnalysisAllSample,
} from '../utils/chemicalAnalysisSamples';
import { useKeyboardShortcuts, commonShortcuts } from '../hooks/useKeyboardShortcuts';
import { showError } from '../utils/toast';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { EmptyState } from '../components/EmptyState';
import { MatchMethodFilter } from '../components/Filters';
import {
  createVolcanoAutocompleteOptions,
  filterVolcanoAutocompleteOptions,
  type VolcanoAutocompleteOption,
  type VolcanoAutocompleteSource,
} from '../utils/volcanoAutocomplete';
import type { Sample } from '../types';
import type { MatchMethod } from '../utils/matchMethod';
import { filterSamplesByMethod, calculateRockTypeDistribution } from '../utils/matchMethod';
import { Combobox } from '../components/Common/Combobox';
import { usePlotTheme } from '../hooks/usePlotTheme';
import { VolcanoSamplesMap } from '../components/Map/VolcanoSamplesMap';

interface ChemicalAnalysisData {
  volcano_number: number;
  volcano_name: string;
  samples_count: number;
  samples?: Array<{} & ChemicalAnalysisAllSample>;
  all_samples?: Array<{} & ChemicalAnalysisAllSample>;
  rock_types: Record<string, number>;
  rock_types_wr: Record<string, number>;  // Rock types for Whole Rock (WR) samples only
}

interface VolcanoSelection {
  name: string;
  number: number;
  data: ChemicalAnalysisData | null;
  samples: Sample[];
  loading: boolean;
  error: string | null;
}

// Compared volcanoes: marks use the theme series colours (hooks/usePlotTheme),
// text uses their readable variants (index.css)
const VOLCANO_TEXT_COLORS = ['rgb(var(--series-1-text))', 'rgb(var(--series-2-text))', 'rgb(var(--series-3-text))'];

const CompareVolcanoesPage: React.FC = () => {
  const { palette: { series } } = usePlotTheme();
  const [volcanoes, setVolcanoes] = useState<VolcanoAutocompleteSource[]>([]);
  
  const [selections, setSelections] = useState<VolcanoSelection[]>([
    { name: '', number: 0, data: null, samples: [], loading: false, error: null },
    { name: '', number: 0, data: null, samples: [], loading: false, error: null },
  ]);
  
  const [searchInputs, setSearchInputs] = useState<string[]>(['', '']);
  
  // Confidence level filter
  const [selectedMatchMethods, setSelectedMatchMethods] = useState<MatchMethod[]>(['literature', 'nearest', 'no_match']);

  // Load volcano names on mount
  useEffect(() => {
    const loadVolcanoes = async () => {
      try {
        const response = await fetch('/api/volcanoes/summary');
        const data = await response.json();
        setVolcanoes(data.data || []);
      } catch (err) {
        console.error('Failed to load volcanoes:', err);
      }
    };
    loadVolcanoes();
  }, []);

  const volcanoOptions = useMemo(
    () => createVolcanoAutocompleteOptions(volcanoes),
    [volcanoes],
  );

  const handleVolcanoSelect = async (index: number, volcano: VolcanoAutocompleteOption) => {
    // Update search input
    const newSearchInputs = [...searchInputs];
    newSearchInputs[index] = volcano.label;
    setSearchInputs(newSearchInputs);

    // Update selection with loading state
    const newSelections = [...selections];
    newSelections[index] = {
      name: volcano.volcano_name,
      number: volcano.volcano_number,
      data: null,
      samples: [],
      loading: true,
      error: null,
    };
    setSelections(newSelections);

    // Fetch data
    try {
      const response = await fetch(
        `/api/volcanoes/${volcano.volcano_number}/chemical-analysis`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch chemical analysis data');
      }

      const data = await response.json();
      const analysisSamples = data.samples && data.samples.length > 0
        ? data.samples
        : data.all_samples || [];
      const samples = transformChemicalAnalysisSamples(analysisSamples);

      newSelections[index] = {
        name: volcano.volcano_name,
        number: volcano.volcano_number,
        data,
        samples,
        loading: false,
        error: null,
      };
      setSelections([...newSelections]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      showError(`Failed to load ${volcano.volcano_name}: ${errorMessage}`);
      newSelections[index] = {
        ...newSelections[index],
        loading: false,
        error: errorMessage,
      };
      setSelections([...newSelections]);
    }
  };

  const handleClearSelection = (index: number) => {
    const newSelections = [...selections];
    newSelections[index] = { name: '', number: 0, data: null, samples: [], loading: false, error: null };
    setSelections(newSelections);

    const newSearchInputs = [...searchInputs];
    newSearchInputs[index] = '';
    setSearchInputs(newSearchInputs);
  };

  const getFilteredVolcanoNames = (index: number) => {
    if (!searchInputs[index]) return [];
    return filterVolcanoAutocompleteOptions(volcanoOptions, searchInputs[index]).slice(0, 10);
  };

  const handleDownloadCSV = () => {
    const allSamples = selections.flatMap(s => s.samples);
    const filteredSamples = filterSamplesByMethod(allSamples, selectedMatchMethods);
    if (filteredSamples.length === 0) return;
    
    const volcanoNamesStr = selections
      .filter(s => s.name)
      .map(s => s.name.replaceAll(' ', '_'))
      .join('_vs_');
    
    exportSamplesToCSV(filteredSamples, `compare_${volcanoNamesStr}.csv`);
  };

  // Keyboard shortcuts
  useKeyboardShortcuts([
    commonShortcuts.download(handleDownloadCSV),
  ]);

  const allSamples = selections.flatMap(s => s.samples);
  const selectedCount = selections.filter(s => s.name).length;
  const isLoading = selections.some(s => s.loading);

  // Memoize expensive chart data preparations to prevent unnecessary re-renders
  const rockTypeChartData = useMemo(() => {
    return selections
      .filter((v: VolcanoSelection) => v.samples && v.samples.length > 0)
      .map((v: VolcanoSelection, idx: number) => {
        // Filter by confidence level AND material=WR for accurate rock type comparison
        const filteredSamples = filterSamplesByMethod(v.samples, selectedMatchMethods);
        const wrSamples = filteredSamples.filter(s => s.material === 'WR');
        const rockTypes = calculateRockTypeDistribution(wrSamples);
        return {
          volcanoName: v.name,
          rockTypes,
          color: series[idx]
        };
      })
      .filter(v => Object.keys(v.rockTypes).length > 0);
  }, [selections, selectedMatchMethods, series]);

  const harkerChartData = useMemo(() => {
    return selections
      .filter((v: VolcanoSelection) => v.samples.length > 0)
      .map((v: VolcanoSelection, idx: number) => ({
        volcanoName: v.name,
        harkerData: filterSamplesByMethod(v.samples, selectedMatchMethods)
          .map(sample => toHarkerDataPoint(sample, v.name))
          .filter((sample): sample is NonNullable<typeof sample> => sample !== null),
        color: series[idx]
      }))
      .filter(v => v.harkerData.length > 0);
  }, [selections, selectedMatchMethods, series]);

  // Memoize sampled data for TAS/AFM plots to improve performance with large datasets
  // Apply confidence filtering
  const sampledSelectionsData = useMemo(() => {
    return selections.map(selection => ({
      ...selection,
      sampledSamples: filterSamplesByMethod(selection.samples, selectedMatchMethods)
    }));
  }, [selections, selectedMatchMethods]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-3">
                <Mountain className="w-8 h-8 text-volcano-600" aria-hidden="true" />
                <h1 className="text-2xl font-bold text-gray-900">Compare Volcanoes</h1>
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Compare chemical compositions side-by-side
              </p>
            </div>
            {selectedCount >= 2 && allSamples.length > 0 && (
              <button
                onClick={handleDownloadCSV}
                className="flex items-center gap-2 px-4 py-2 bg-volcano-600 text-white rounded-lg hover:bg-volcano-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-volcano-500 focus:ring-offset-2"
                aria-label="Download Combined CSV of the comparison data"
                title="Download CSV (Ctrl+D / Cmd+D)"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
                Download Combined CSV
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Volcano Selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {selections.map((selection, index) => (
            <div key={`selector-${index}`} className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <span className="series-swatch" style={{ backgroundColor: series[index] }} aria-hidden="true" />
                  Volcano {index + 1}
                </h2>
                {selection.name && (
                  <button
                    onClick={() => handleClearSelection(index)}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Clear selection"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
              
              <div className="relative">
                <Combobox
                  value={searchInputs[index]}
                  onValueChange={(value) =>
                    setSearchInputs((prev) => prev.map((input, i) => (i === index ? value : input)))
                  }
                  options={getFilteredVolcanoNames(index)}
                  getOptionKey={(volcano) => volcano.volcano_number}
                  getOptionLabel={(volcano) => volcano.label}
                  onSelect={(volcano) => handleVolcanoSelect(index, volcano)}
                  placeholder="Type to search volcanoes..."
                  ariaLabel={`Search for volcano ${index + 1}`}
                  inputClassName="field"
                />
              </div>

              {selection.loading && (
                <div className="mt-4">
                  <CardSkeleton />
                </div>
              )}

              {selection.error && (
                <div className="mt-4 text-sm text-red-600">{selection.error}</div>
              )}

              {selection.data && (() => {
                const filteredSamples = filterSamplesByMethod(selection.samples, selectedMatchMethods);
                const tasCount = filteredSamples.filter(hasTasOxides).length;
                const afmCount = filteredSamples.filter(hasAfmOxides).length;
                const totalTasCount = selection.samples.filter(hasTasOxides).length;
                const totalAfmCount = selection.samples.filter(hasAfmOxides).length;
                return (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs text-gray-600">Samples</p>
                      <p className="text-lg font-bold" style={{ color: VOLCANO_TEXT_COLORS[index] }}>
                        {filteredSamples.length}
                      </p>
                      {filteredSamples.length < selection.samples.length && (
                        <p className="text-xs text-gray-500">of {selection.samples.length}</p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs text-gray-600">TAS Points</p>
                      <p className="text-lg font-bold" style={{ color: VOLCANO_TEXT_COLORS[index] }}>
                        {tasCount}
                      </p>
                      {filteredSamples.length < selection.samples.length && (
                        <p className="text-xs text-gray-500">of {totalTasCount}</p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs text-gray-600">AFM Points</p>
                      <p className="text-lg font-bold" style={{ color: VOLCANO_TEXT_COLORS[index] }}>
                        {afmCount}
                      </p>
                      {filteredSamples.length < selection.samples.length && (
                        <p className="text-xs text-gray-500">of {totalAfmCount}</p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>

        {/* Confidence Level Filter */}
        {selectedCount >= 2 && !isLoading && allSamples.length > 0 && (
          <MatchMethodFilter
            selectedMethods={selectedMatchMethods}
            onChange={setSelectedMatchMethods}
            className="mb-6"
          />
        )}

        {/* Rock Type Distribution - Combined Chart for All Volcanoes */}
        {selectedCount >= 2 && !isLoading && rockTypeChartData.length > 0 && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Rock Type Distribution Comparison</h2>
            <RockTypeDistributionChart volcanoes={rockTypeChartData} />
          </div>
        )}

        {/* Harker Diagrams - Major Oxide Variations (Lazy Loaded) */}
        {selectedCount >= 2 && !isLoading && harkerChartData.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Harker Variation Diagrams</h2>
            </div>
            <HarkerDiagrams volcanoes={harkerChartData} />
          </div>
        )}

        {/* Side-by-Side Comparison */}
        {selectedCount >= 2 && !isLoading && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {sampledSelectionsData.filter(s => s.name && s.data).map((selection, index) => (
              <div 
                key={selection.number}
                className="card p-6"
              >
                {/* Volcano Header */}
                <div className="mb-6">
                  <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900 mb-2">
                    <span className="series-swatch" style={{ backgroundColor: series[index] }} aria-hidden="true" />
                    {selection.name}
                  </h2>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-xs text-gray-600 mb-1">Filtered Samples</p>
                      <p className="text-xl font-bold" style={{ color: VOLCANO_TEXT_COLORS[index] }}>
                        {selection.sampledSamples.length}
                      </p>
                      {selection.sampledSamples.length < selection.samples.length && (
                        <p className="text-xs text-gray-500">of {selection.samples.length} total</p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-xs text-gray-600 mb-1">TAS Data</p>
                      <p className="text-xl font-bold" style={{ color: VOLCANO_TEXT_COLORS[index] }}>
                        {selection.sampledSamples.filter(hasTasOxides).length}
                      </p>
                      {selection.sampledSamples.length < selection.samples.length && (
                        <p className="text-xs text-gray-500">of {selection.samples.filter(hasTasOxides).length} total</p>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <p className="text-xs text-gray-600 mb-1">AFM Data</p>
                      <p className="text-xl font-bold" style={{ color: VOLCANO_TEXT_COLORS[index] }}>
                        {selection.sampledSamples.filter(hasAfmOxides).length}
                      </p>
                      {selection.sampledSamples.length < selection.samples.length && (
                        <p className="text-xs text-gray-500">of {selection.samples.filter(hasAfmOxides).length} total</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Location of the volcano and of its samples */}
                <div className="mb-6">
                  <h3 className="eyebrow mb-3">Location and Samples</h3>
                  <VolcanoSamplesMap
                    volcanoNumber={selection.number}
                    volcanoName={selection.name}
                    matchMethods={selectedMatchMethods}
                    height={260}
                  />
                </div>

                {/* TAS Plot */}
                <div className="mb-6">
                  <h3 className="eyebrow mb-3">
                    TAS Diagram
                    {selection.samples.length > 1000 && (
                      <span className="ml-2 text-xs text-gray-500">
                        (showing {selection.sampledSamples.length} of {selection.samples.length} samples)
                      </span>
                    )}
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden h-[580px] sm:h-[400px]">
                    <TASPlot samples={selection.sampledSamples} />
                  </div>
                </div>

                {/* AFM Plot */}
                <div>
                  <h3 className="eyebrow mb-3">
                    AFM Diagram
                    {selection.samples.length > 1000 && (
                      <span className="ml-2 text-xs text-gray-500">
                        (showing {selection.sampledSamples.length} of {selection.samples.length} samples)
                      </span>
                    )}
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden h-[580px] sm:h-[400px]">
                    <AFMPlot samples={selection.sampledSamples} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        )}

        {/* Empty State */}
        {selectedCount < 2 && !isLoading && (
          <EmptyState
            icon={Mountain}
            title="Select 2 Volcanoes to Compare"
            description="Choose volcanoes from the selectors above to view their side-by-side chemical comparison with TAS and AFM diagrams."
          />
        )}
      </div>
    </div>
  );
};

export default CompareVolcanoesPage;
